import { useEffect, useState } from 'react'

/** Editable elements whose focus brings up the on-screen keyboard. */
const isEditable = (el: Element | null): boolean => {
  if (!el) return false
  if (el.tagName === 'TEXTAREA') return true
  if (el.tagName === 'INPUT') {
    const type = (el.getAttribute('type') || 'text').toLowerCase()
    return !['button', 'submit', 'reset', 'checkbox', 'radio', 'file', 'range', 'color', 'image'].includes(type)
  }
  return (el as HTMLElement).isContentEditable === true
}

/**
 * True while a text field is focused — i.e. the on-screen keyboard is up.
 *
 * Focus is the deterministic signal: on touch devices the soft keyboard shows
 * exactly when an editable element is focused. (An earlier version inferred this
 * from visualViewport.height vs window.innerHeight, but iOS reports those
 * inconsistently — innerHeight itself shifts with both the keyboard and the
 * collapsing toolbars — so detection fired erratically, especially right after
 * load.) Lets the UI reclaim space the keyboard covers, e.g. the mobile bottom
 * nav.
 */
export const useKeyboardOpen = (): boolean => {
  const [open, setOpen] = useState(() => isEditable(document.activeElement))

  useEffect(() => {
    const onFocusIn = (e: FocusEvent) => {
      if (isEditable(e.target as Element)) setOpen(true)
    }
    // On blur focus may be moving to another field — re-check on the next tick.
    const onFocusOut = () => {
      window.setTimeout(() => setOpen(isEditable(document.activeElement)), 0)
    }
    document.addEventListener('focusin', onFocusIn)
    document.addEventListener('focusout', onFocusOut)
    return () => {
      document.removeEventListener('focusin', onFocusIn)
      document.removeEventListener('focusout', onFocusOut)
    }
  }, [])

  return open
}
